/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Fragment } from 'react';

import { Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import GradeNotification from 'bundles/compound-assessments/components/form-parts/GradeNotification';
import ValidationError from 'bundles/compound-assessments/components/form-parts/ValidationError';
import Option from 'bundles/compound-assessments/components/form-parts/checkbox/Option';
import type { FormPartsValidationStatus } from 'bundles/compound-assessments/components/form-parts/lib/checkResponsesInvalid';
import { FormPartsValidationStatuses } from 'bundles/compound-assessments/components/form-parts/lib/constants';
import { isResponseInvalid } from 'bundles/compound-assessments/components/form-parts/multipleFillableBlanks/utils';
import type {
  MultipleFillableBlanksPrompt,
  MultipleFillableBlanksResponse,
} from 'bundles/compound-assessments/types/FormParts';

import _t from 'i18n!nls/compound-assessments';

export type Props = {
  showValidation: boolean;
  prompt?: MultipleFillableBlanksPrompt;
  response?: MultipleFillableBlanksResponse;
};

const styles = {
  responseHeading: () => css`
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-200);
  `,
  validationError: css({
    transform: 'translate(-10px)',
  }),
  optionContainer: (theme: Theme) =>
    css({
      ':not(:first-child)': {
        marginTop: 'var(--cds-spacing-150)',
      },
    }),
};

export const checkInvalid = (
  response?: MultipleFillableBlanksResponse,
  prompt?: MultipleFillableBlanksPrompt
): FormPartsValidationStatus | null => {
  return isResponseInvalid(response, prompt) ? FormPartsValidationStatuses.warning : null;
};

const MultipleFillableBlanks: React.FC<Props> = ({ showValidation, prompt, response }) => {
  const isInvalid = showValidation && isResponseInvalid(response, prompt);

  if (prompt) {
    const showFeedback = !!prompt?.feedback;
    const { fillableBlanks = [] } = prompt.variant.definition;

    return (
      <div>
        {showFeedback &&
          fillableBlanks.map((fillableBlank, index) => {
            const options = fillableBlank?.definition?.answerOptions;
            const fillableBlankResponse =
              response?.definition?.value?.fillableBlankResponses?.[fillableBlank?.definition?.id];

            return (
              <Fragment>
                <Typography2
                  component="h3"
                  css={styles.responseHeading}
                  variant="subtitleMedium"
                  data-testid="response-heading"
                >
                  {_t('Response #{responseNumber}', { responseNumber: index + 1 })}
                </Typography2>
                {options.map((option) => {
                  const chosenAnswerOptionId = fillableBlankResponse?.definition?.response || '';

                  return (
                    <div key={option.answerOptionId} css={styles.optionContainer}>
                      <Option
                        onChange={() => undefined}
                        option={{ ...option, id: option.answerOptionId }}
                        isSelected={chosenAnswerOptionId === option.answerOptionId}
                        isDisabled={true}
                        isReadOnly={true}
                        isRadio={true}
                        promptId={prompt.id}
                      />
                    </div>
                  );
                })}
                <GradeNotification prompt={prompt} fillableBlank={fillableBlank} />
              </Fragment>
            );
          })}
        {isInvalid && (
          <div css={styles.validationError}>
            <ValidationError id={prompt.id} />
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default MultipleFillableBlanks;
